import React, { useState, useEffect } from 'react';
import { trackLead } from '../utils/tracking';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiMapPin, FiChevronLeft, FiChevronRight, FiX, FiChevronRight as FiCrumb, FiCheck, FiSend,
} from 'react-icons/fi';
import { LuBed, LuBath, LuRuler, LuCar } from 'react-icons/lu';
import { propertyService, buildImageUrl } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import Header from '../components/Header';
import Footer from '../components/layout/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import WhatsAppIcon from '../components/icons/WhatsAppIcon';
import { WhatsAppService } from '../utils/whatsapp';
import { sendSiteEvent } from '../utils/siteEvents';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { translateFeature, translatePropertyType } from '../utils/translations';
import BrokerContactButtons from '../components/BrokerContactButtons';
import VideoPlayer from '../components/property/VideoPlayer';
import PropertyLocationMap from '../components/property/PropertyLocationMap';
import SimilarProperties from '../components/property/SimilarProperties';
import { coordinatesOf } from '../components/property/propertyMap';
import { getDisplayPricing } from '../components/PropertyCard';
import { getUnavailability } from '../utils/availability';
import {
  Container, Button, Input, Textarea, Field, Label, Badge, Spinner,
} from '../components/ui';
import Seo, { organizationSchema } from '../components/Seo';
import { useSettings } from '../contexts/SettingsContext';
import { isMissingHttpError } from '../utils/http';

const Page = styled.div`
  background: ${({ theme }) => theme.colors.background};
  min-height: 70vh;
`;

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: 1.25rem 0 0.25rem;

  a:hover { color: ${({ theme }) => theme.colors.primary}; }
  svg { color: ${({ theme }) => theme.colors.neutral[400]}; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 40ch; }
`;

/* ------------------------------- Gallery ------------------------------- */

const Gallery = styled.div`
  margin: 1rem 0 2rem;
`;

const MainImage = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutral[100]};
  cursor: zoom-in;

  img { width: 100%; height: 100%; object-fit: cover; }
`;

/* --------------------- Imóvel vendido / alugado --------------------- */

/* Escurece a foto o suficiente para a tarja ler, sem esconder o imóvel. */
const UnavailableVeil = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  pointer-events: none;
`;

const UnavailableStamp = styled.div<{ $kind: 'sold' | 'rented' }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-8deg);
  padding: 0.7rem 2.5rem;
  border: 4px solid #fff;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $kind }) => ($kind === 'sold' ? 'rgba(190, 18, 60, 0.92)' : 'rgba(15, 23, 42, 0.92)')};
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.5rem, 4.5vw, 2.75rem);
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  text-align: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.35);
  pointer-events: none;
`;

const UnavailableNotice = styled.div<{ $kind: 'sold' | 'rented' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 5px solid ${({ $kind }) => ($kind === 'sold' ? '#be123c' : '#1e293b')};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};

  strong {
    color: ${({ theme }) => theme.colors.foreground};
  }

  span {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  a {
    margin-left: auto;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.primary};
    white-space: nowrap;

    &:hover { text-decoration: underline; }
  }
`;

const UnavailableTag = styled.span<{ $kind: 'sold' | 'rented' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $kind }) => ($kind === 'sold' ? '#be123c' : '#1e293b')};
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

/* Mantem a tarja ancorada na foto tambem em tela cheia. */
const LbFrame = styled.div`
  position: relative;
  display: inline-flex;
`;

const Counter = styled.div`
  position: absolute;
  bottom: 0.85rem;
  right: 0.85rem;
  background: rgba(15, 23, 42, 0.7);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
`;

const NavBtn = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 0.85rem;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.92);
  color: ${({ theme }) => theme.colors.foreground};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: background 0.2s ease;

  &:hover { background: #fff; }
`;

const Thumbs = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
`;

const Thumb = styled.button<{ $active: boolean }>`
  flex: 0 0 auto;
  width: 96px;
  height: 70px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  overflow: hidden;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  padding: 0;
  cursor: pointer;
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  transition: opacity 0.2s ease;

  &:hover { opacity: 1; }
  img { width: 100%; height: 100%; object-fit: cover; }
`;

/* -------------------------------- Layout ------------------------------- */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.75fr 1fr;
  gap: 2.5rem;
  padding-bottom: 4rem;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const TitleRow = styled.div`
  margin-bottom: 1.25rem;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.2;
`;

const LocationLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  svg { color: ${({ theme }) => theme.colors.neutral[400]}; }
`;

const QuickSpecs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.1rem 1.25rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: 2rem;
`;

const QuickSpec = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.foreground};
  font-weight: 600;
  font-size: 0.95rem;

  svg { color: ${({ theme }) => theme.colors.primary}; }
  small { color: ${({ theme }) => theme.colors.text.secondary}; font-weight: 500; }
`;

const Block = styled.section`
  margin-bottom: 2.25rem;
`;

const BlockTitle = styled.h2`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.9rem;
`;

const Para = styled.p`
  color: ${({ theme }) => theme.colors.neutral[700]};
  line-height: 1.75;
  white-space: pre-line;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
`;

const DetailItem = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 0.75rem 0.9rem;

  .label { font-size: 0.78rem; color: ${({ theme }) => theme.colors.text.secondary}; }
  .value { font-weight: 600; color: ${({ theme }) => theme.colors.foreground}; margin-top: 0.15rem; }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;

  @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const AddressList = styled.div`
  p { color: ${({ theme }) => theme.colors.neutral[700]}; margin-bottom: 0.4rem; }
  strong { color: ${({ theme }) => theme.colors.foreground}; }
`;

const Features = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FeatureChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.neutral[700]};
  padding: 0.45rem 0.8rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.85rem;
  font-weight: 500;

  svg { color: ${({ theme }) => theme.colors.primary}; }
`;

/* ------------------------------- Sidebar ------------------------------- */

const Sidebar = styled.aside`
  position: sticky;
  top: 96px;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 940px) {
    position: static;
  }
`;

const SideCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 1.4rem;
`;

const PriceLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Price = styled.div`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  line-height: 1.1;
  margin-top: 0.2rem;
`;

const Broker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
`;

const BrokerAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primaryDark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const BrokerName = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;
const BrokerMeta = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FormTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin-bottom: 0.3rem;
`;
const FormHint = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 1rem;
`;
const FormStack = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 5rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ------------------------------- Lightbox ------------------------------ */

const Lightbox = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.92);
  align-items: center;
  justify-content: center;
  padding: 2rem;

  img { max-width: 92vw; max-height: 88vh; object-fit: contain; border-radius: 6px; }
`;

const LbClose = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255, 255, 255, 0.28); }
`;

const LbNav = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 1.5rem;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255, 255, 255, 0.28); }
`;

const PropertyDetailPage: React.FC = () => {
  const { siteSettings } = useSettings();
  const { id } = useParams<{ id: string }>();
  const { showNotification } = useNotification();
  const [property, setProperty] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  useEffect(() => {
    loadProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      setProperty(null);
      setMissing(false);
      setLoadFailed(false);
      const data = await propertyService.getById(id!);
      setProperty(data);
      setCurrentImageIndex(0);
      sendSiteEvent('property_view');
    } catch (error) {
      if (isMissingHttpError(error)) {
        setMissing(true);
      } else {
        setLoadFailed(true);
      }
      showNotification('Erro ao carregar propriedade', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await propertyService.sendMessage(id!, formData);
      trackLead('property_form');
      showNotification('Mensagem enviada com sucesso', 'success');
      const leadData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        message: formData.message, propertyId: property?.id?.toString(), propertyTitle: property?.title,
      };
      setTimeout(() => WhatsAppService.showWhatsAppOption(leadData), 1000);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      showNotification('Erro ao enviar mensagem', 'error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const unavailability = getUnavailability(property);

  // A capa é escolha independente da sequência: ela abre a galeria e o resto
  // segue a ordem definida no admin. Sem isso, o card da listagem (que usa
  // cover_image) e esta página mostram fotos diferentes.
  const images: string[] = React.useMemo(() => {
    const gallery: string[] = property && Array.isArray(property.images)
      ? property.images
          .map((image: unknown): string | undefined => {
            if (typeof image === 'string') return image;
            if (image && typeof image === 'object' && 'url' in image) {
              const url = (image as { url?: unknown }).url;
              return typeof url === 'string' ? url : undefined;
            }
            return undefined;
          })
          .filter((url: string | undefined): url is string => typeof url === 'string' && url.length > 0)
      : [];
    const cover = property?.cover_image;
    if (!cover || !gallery.includes(cover)) return gallery;
    return [cover, ...gallery.filter((url) => url !== cover)];
  }, [property]);
  const nextImage = () => images.length && setCurrentImageIndex((p) => (p === images.length - 1 ? 0 : p + 1));
  const prevImage = () => images.length && setCurrentImageIndex((p) => (p === 0 ? images.length - 1 : p - 1));
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, images.length]);

  useScrollToTop(id);

  if (loading) {
    return (
      <Page>
        <Header />
        <Center>
          <Spinner $size={36} />
          <p>Carregando detalhes do imóvel...</p>
        </Center>
        <Footer />
      </Page>
    );
  }

  if (!property) {
    return (
      <Page>
        {missing && (
          <Seo
            title="Imóvel não encontrado"
            description="O imóvel solicitado não foi encontrado ou não está mais publicado."
            canonicalPath={`/imovel/${id || ''}`}
            robots="noindex,nofollow"
          />
        )}
        <Header />
        <Center>
          <h2>{loadFailed ? 'Não foi possível carregar o imóvel' : 'Imóvel não encontrado'}</h2>
          <p>{loadFailed
            ? 'Tivemos uma instabilidade temporária. Tente novamente em alguns instantes.'
            : 'O imóvel que você procura não está mais disponível.'}</p>
          {loadFailed ? (
            <Button type="button" $variant="primary" onClick={loadProperty}>Tentar novamente</Button>
          ) : (
            <Button as={Link} to="/imoveis" $variant="primary">Ver outros imóveis</Button>
          )}
        </Center>
        <Footer />
      </Page>
    );
  }

  const isRent = property.transaction_type === 'aluguel';
  const isMixed = property.transaction_type === 'venda_aluguel';
  const pricingInfos = getDisplayPricing(property);
  const formatPrice = (value: number | null) => value !== null && value > 0
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })
    : 'Sob consulta';
  const locationStr = property.address
    ? `${property.address.neighborhood || ''}${property.address.neighborhood ? ', ' : ''}${property.address.city || ''}${property.address.state ? '/' + property.address.state : ''}`
    : property.location || 'Localização sob consulta';

  // Specs detalhados (CanalPro)
  const details: Array<{ label: string; value: string }> = [];
  if (property.type) details.push({ label: 'Tipo', value: translatePropertyType(property.type) });
  if (property.suites > 0) details.push({ label: 'Suítes', value: String(property.suites) });
  if (Number.isFinite(property.unit_floor) && property.unit_floor >= 0)
    details.push({ label: 'Andar', value: property.unit_floor === 0 ? 'Térreo' : `${property.unit_floor}º` });
  if (property.floors > 0) details.push({ label: 'Andares', value: String(property.floors) });
  if (property.total_areas > 0) details.push({ label: 'Área total', value: `${property.total_areas} m²` });
  if (property.usable_areas > 0) details.push({ label: 'Área útil', value: `${property.usable_areas} m²` });
  if (property.buildings > 0) details.push({ label: 'Blocos', value: String(property.buildings) });
  if (property.units_on_the_floor > 0) details.push({ label: 'Unid./andar', value: String(property.units_on_the_floor) });

  // Features
  let featuresArray: string[] = [];
  if (typeof property.features === 'string' && property.features.trim())
    featuresArray = property.features.split(',').map((f: string) => f.trim()).filter(Boolean);
  else if (Array.isArray(property.features))
    featuresArray = property.features.filter((f: any) => f && typeof f === 'string').map((f: string) => f.trim());

  const broker = property.managed_by || property.captured_by;
  const brokerInitials = broker?.name
    ? broker.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'CR';

  const hasCosts = property.monthly_condo_fee > 0 || property.iptu > 0 || property.yearly_iptu > 0;

  return (
    <Page>
      <Seo
        title={`${property.title} | ${siteSettings?.company_info?.name || siteSettings?.site_name || 'Imobiliária'}`}
        description={`${translatePropertyType(property.type)} ${isRent ? 'para alugar' : isMixed ? 'para venda ou aluguel' : 'à venda'}${locationStr ? ` em ${locationStr}` : ''}. ${property.area ? `${property.area} m². ` : ''}${(property.description || '').replace(/\s+/g, ' ')}`.slice(0, 158)}
        canonicalPath={`/imovel/${property.id}`}
        image={images[0] ? buildImageUrl(images[0]) : undefined}
        type="article"
        structuredData={[
          organizationSchema(siteSettings),
          {
            '@type': 'RealEstateListing',
            '@id': `${window.location.origin}/imovel/${property.id}#listing`,
            name: property.title,
            url: `${window.location.origin}/imovel/${property.id}`,
            description: property.description,
            image: images.map(buildImageUrl),
            provider: { '@id': `${window.location.origin}/#organization` },
            offers: {
              '@type': 'Offer',
              price: property.show_price === false ? undefined : property.price,
              priceCurrency: property.show_price === false ? undefined : 'BRL',
              availability: unavailability ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock',
            },
          },
        ]}
      />
      <Header />
      <WhatsAppButton propertyId={property?.id?.toString()} propertyTitle={property?.title} />

      <Container>
        <Crumbs>
          <Link to="/">Início</Link>
          <FiCrumb size={14} />
          <Link to="/imoveis">Imóveis</Link>
          <FiCrumb size={14} />
          <span>{property.title}</span>
        </Crumbs>

        {images.length > 0 && (
          <Gallery>
            <MainImage onClick={openModal}>
              <img src={buildImageUrl(images[currentImageIndex])} alt={property.title} />
              {unavailability && (
                <>
                  <UnavailableVeil />
                  <UnavailableStamp $kind={unavailability.kind}>
                    {unavailability.label}
                  </UnavailableStamp>
                </>
              )}
              <Counter>{currentImageIndex + 1} / {images.length}</Counter>
              {images.length > 1 && (
                <>
                  <NavBtn $side="left" onClick={(e) => { e.stopPropagation(); prevImage(); }} aria-label="Anterior">
                    <FiChevronLeft size={22} />
                  </NavBtn>
                  <NavBtn $side="right" onClick={(e) => { e.stopPropagation(); nextImage(); }} aria-label="Próxima">
                    <FiChevronRight size={22} />
                  </NavBtn>
                </>
              )}
            </MainImage>
            {images.length > 1 && (
              <Thumbs>
                {images.map((image, index) => (
                  <Thumb key={index} $active={index === currentImageIndex} onClick={() => setCurrentImageIndex(index)}>
                    <img src={buildImageUrl(image)} alt={`Foto ${index + 1}`} />
                  </Thumb>
                ))}
              </Thumbs>
            )}
          </Gallery>
        )}

        {unavailability && (
          <UnavailableNotice $kind={unavailability.kind}>
            <UnavailableTag $kind={unavailability.kind}>{unavailability.label}</UnavailableTag>
            <strong>{unavailability.message}</strong>
            <span>Você ainda pode ver as fotos e os detalhes.</span>
            <Link to="/imoveis">Ver imóveis disponíveis</Link>
          </UnavailableNotice>
        )}

        <Layout>
          <Main>
            <TitleRow>
              {unavailability ? (
                <UnavailableTag $kind={unavailability.kind}>{unavailability.label}</UnavailableTag>
              ) : (
                <Badge $variant="brand">{isMixed ? 'Venda e locação' : isRent ? 'Para alugar' : 'À venda'}</Badge>
              )}
              <Title style={{ marginTop: '0.6rem' }}>{property.title}</Title>
              <LocationLine>
                <FiMapPin size={16} /> {locationStr}
              </LocationLine>
            </TitleRow>

            <QuickSpecs>
              {property.bedrooms > 0 && (
                <QuickSpec><LuBed size={20} /> {property.bedrooms} <small>quartos</small></QuickSpec>
              )}
              {property.bathrooms > 0 && (
                <QuickSpec><LuBath size={20} /> {property.bathrooms} <small>banheiros</small></QuickSpec>
              )}
              {property.area > 0 && (
                <QuickSpec><LuRuler size={20} /> {property.area} <small>m²</small></QuickSpec>
              )}
              {property.parking_spaces >= 0 && property.parking_spaces !== undefined && (
                <QuickSpec><LuCar size={20} /> {property.parking_spaces} <small>vagas</small></QuickSpec>
              )}
            </QuickSpecs>

            {property.description && (
              <Block>
                <BlockTitle>Sobre o imóvel</BlockTitle>
                <Para>{property.description}</Para>
              </Block>
            )}

            {property.videos && property.videos.length > 0 && (
              <Block>
                <VideoPlayer videos={property.videos} featuredVideoId={property.featured_video_id} />
              </Block>
            )}

            {details.length > 0 && (
              <Block>
                <BlockTitle>Detalhes</BlockTitle>
                <DetailGrid>
                  {details.map((d, i) => (
                    <DetailItem key={i}>
                      <div className="label">{d.label}</div>
                      <div className="value">{d.value}</div>
                    </DetailItem>
                  ))}
                </DetailGrid>
              </Block>
            )}

            <TwoCol>
              {hasCosts && (
                <Block>
                  <BlockTitle>Custos e taxas</BlockTitle>
                  <AddressList>
                    {property.monthly_condo_fee > 0 && (
                      <p><strong>Condomínio:</strong> R$ {property.monthly_condo_fee.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/mês</p>
                    )}
                    {property.iptu > 0 && (
                      <p><strong>IPTU:</strong> R$ {property.iptu.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}{property.iptu_period === 'MONTHLY' ? '/mês' : property.iptu_period === 'YEARLY' ? '/ano' : ''}</p>
                    )}
                    {property.yearly_iptu > 0 && !property.iptu && (
                      <p><strong>IPTU anual:</strong> R$ {property.yearly_iptu.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/ano</p>
                    )}
                  </AddressList>
                </Block>
              )}

              <Block>
                <BlockTitle>Localização</BlockTitle>
                <AddressList>
                  {property.condominium_name && (
                    <p><strong>Condomínio / empreendimento:</strong> {property.condominium_name}</p>
                  )}
                  {property.address && typeof property.address === 'object' ? (
                    <>
                      {property.address.complement && <p><strong>Complemento:</strong> {property.address.complement}</p>}
                      {property.address.neighborhood && <p><strong>Bairro:</strong> {property.address.neighborhood}</p>}
                      {property.address.city && <p><strong>Cidade:</strong> {property.address.city}</p>}
                      {property.address.state && <p><strong>Estado:</strong> {property.address.state}</p>}
                      {property.address.zipcode && <p><strong>CEP:</strong> {property.address.zipcode}</p>}
                    </>
                  ) : (
                    <p>{property.location || 'Localização não informada'}</p>
                  )}
                </AddressList>
              </Block>
            </TwoCol>

            {coordinatesOf(property.address) && (
              <Block>
                <BlockTitle>Onde fica</BlockTitle>
                <PropertyLocationMap
                  address={property.address}
                  type={property.type}
                  title={property.title}
                />
              </Block>
            )}

            {featuresArray.length > 0 && (
              <Block>
                <BlockTitle>Características</BlockTitle>
                <Features>
                  {featuresArray.map((feature, index) => (
                    <FeatureChip key={index}>
                      <FiCheck size={14} /> {translateFeature(feature)}
                    </FeatureChip>
                  ))}
                </Features>
              </Block>
            )}
          </Main>

          <Sidebar>
            <SideCard>
              {pricingInfos.map(item => (
                <div key={item.business_type}>
                  <PriceLabel>{item.business_type === 'RENTAL' ? 'Locação / mês' : 'Valor de venda'}</PriceLabel>
                  <Price>{formatPrice(item.price)}</Price>
                </div>
              ))}
            </SideCard>

            {broker && (
              <SideCard>
                <Broker>
                  <BrokerAvatar>
                    {broker.photo ? <img src={buildImageUrl(broker.photo)} alt={broker.name} /> : brokerInitials}
                  </BrokerAvatar>
                  <div>
                    <BrokerName>{broker.name}</BrokerName>
                    <BrokerMeta>
                      {broker.is_team_leader ? 'Corretor líder' : 'Corretor'}
                      {broker.creci ? ` · CRECI ${broker.creci}` : ''}
                    </BrokerMeta>
                  </div>
                </Broker>
                <BrokerContactButtons broker={broker} propertyTitle={property.title} />
              </SideCard>
            )}

            <SideCard>
              <FormTitle>
                {unavailability ? 'Procura algo parecido?' : 'Tenho interesse'}
              </FormTitle>
              <FormHint>
                {unavailability
                  ? `Este imóvel já foi ${unavailability.kind === 'sold' ? 'vendido' : 'alugado'}, mas podemos indicar opções semelhantes.`
                  : 'Preencha e nossa equipe responde em até 24 horas.'}
              </FormHint>
              <FormStack onSubmit={handleSubmit}>
                <Field>
                  <Label>Nome</Label>
                  <Input type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Field>
                <Field>
                  <Label>E-mail</Label>
                  <Input type="email" name="email" value={formData.email} onChange={handleChange} required />
                </Field>
                <Field>
                  <Label>Telefone</Label>
                  <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                </Field>
                <Field>
                  <Label>Mensagem</Label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={unavailability
                      ? `Olá! Vi o imóvel "${property.title}" e gostaria de saber se há algo parecido disponível.`
                      : `Olá! Tenho interesse no imóvel "${property.title}". Gostaria de agendar uma visita.`}
                    required
                  />
                </Field>
                <Button type="submit" $variant="primary" $full>
                  <FiSend size={16} /> Enviar mensagem
                </Button>
                <Button
                  type="button"
                  $variant="whatsapp"
                  $full
                  onClick={() => {
                    if (property) {
                      WhatsAppService.sendToWhatsApp({
                        name: 'Cliente Interessado', email: '', phone: '',
                        propertyId: property.id.toString(), propertyTitle: property.title,
                      });
                    }
                  }}
                >
                  <WhatsAppIcon size={18} color="white" /> WhatsApp direto
                </Button>
              </FormStack>
            </SideCard>
          </Sidebar>
        </Layout>

        <SimilarProperties property={property} unavailable={Boolean(unavailability)} />
      </Container>

      <Lightbox $open={isModalOpen} onClick={closeModal}>
        <LbClose onClick={closeModal} aria-label="Fechar"><FiX size={22} /></LbClose>
        {images.length > 0 && (
          <LbFrame onClick={(e) => e.stopPropagation()}>
            <img src={buildImageUrl(images[currentImageIndex])} alt={property.title} />
            {unavailability && (
              <UnavailableStamp $kind={unavailability.kind}>
                {unavailability.label}
              </UnavailableStamp>
            )}
          </LbFrame>
        )}
        {images.length > 1 && (
          <>
            <LbNav $side="left" onClick={(e) => { e.stopPropagation(); prevImage(); }} aria-label="Anterior">
              <FiChevronLeft size={26} />
            </LbNav>
            <LbNav $side="right" onClick={(e) => { e.stopPropagation(); nextImage(); }} aria-label="Próxima">
              <FiChevronRight size={26} />
            </LbNav>
          </>
        )}
      </Lightbox>

      <Footer />
    </Page>
  );
};

export default PropertyDetailPage;
